import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { CopyInput } from './components/CopyInput';
import { BrandTerms } from './components/BrandTerms';
import { TalkingPoints } from './components/TalkingPoints';
import { ChannelPolicy } from './components/ChannelPolicy';
import { ReadabilityScore } from './components/ReadabilityScore';
import { BlockedMessage } from './components/BlockedMessage';
import { ScoreAdditionalFactors } from './components/ScoreAdditionalFactors';
import { ScoringMethod } from './components/ScoringMethod';
import { Histograms } from './components/Histograms';
import { SEOTracker } from './components/SEOTracker';
import { PlagiarismChecker } from './components/PlagiarismChecker';
import { Benchmark } from './components/Benchmark';
import { useStyles } from './useStyles';

export const TextDetailsPanel = ({
  className,
  classes,
  copyInputProps,
  scoreDetailsProps,
  scoreAdditionalFactorsProps,
  audienceChartProps,
  histogramAgeScoreProps,
  histogramGenderScoreProps,
  talkingPointsProps,
  brandTermsProps,
  channelPolicyProps,
  blockedProps,
  readabilityScoreProps,
  seoTrackerProps,
  plagiarismCheckerProps,
  benchmarkProps,
}) => {
  const styles = useStyles({ classes });
  const displayHistograms = histogramAgeScoreProps || histogramGenderScoreProps;

  return (
    <div
      className={cx(styles.root, className)}
      data-testid="TextDetailsPanel"
    >
      {scoreDetailsProps && <ScoringMethod {...scoreDetailsProps} />}

      {copyInputProps && (
        <CopyInput
          className={styles.textEditorWrapper}
          {...copyInputProps}
        />
      )}

      {benchmarkProps && <Benchmark {...benchmarkProps} />}

      <div className={styles.detailsBody}>
        {displayHistograms && (
          <Histograms
            classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
            audienceChartProps={audienceChartProps}
            histogramAgeScoreProps={histogramAgeScoreProps}
            histogramGenderScoreProps={histogramGenderScoreProps}
          />
        )}

        {scoreAdditionalFactorsProps && <ScoreAdditionalFactors {...scoreAdditionalFactorsProps} />}

        {talkingPointsProps && (
          <TalkingPoints
            classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
            {...talkingPointsProps}
          />
        )}

        {readabilityScoreProps && <ReadabilityScore {...readabilityScoreProps} />}

        {plagiarismCheckerProps && <PlagiarismChecker {...plagiarismCheckerProps} />}

        {seoTrackerProps && <SEOTracker {...seoTrackerProps} />}

        {brandTermsProps && (
          <BrandTerms
            classes={{ root: styles.groupContainer, boxGroupTitle: styles.boxGroupTitle }}
            {...brandTermsProps}
          />
        )}

        {channelPolicyProps && (
          <ChannelPolicy
            classes={{
              root: styles.groupContainer,
              boxGroupTitle: styles.boxGroupTitle,
              content: styles.channelPolicyContent,
            }}
            {...channelPolicyProps}
          />
        )}
      </div>

      {blockedProps && (
        <div className={styles.blurred}>
          <BlockedMessage
            className={styles.blockedMessage}
            {...blockedProps}
          />
        </div>
      )}
    </div>
  );
};

TextDetailsPanel.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  copyInputProps: PropTypes.shape({}),
  scoreDetailsProps: PropTypes.shape({}),
  scoreAdditionalFactorsProps: PropTypes.shape({}),
  audienceChartProps: PropTypes.shape({}),
  histogramAgeScoreProps: PropTypes.shape({}),
  histogramGenderScoreProps: PropTypes.shape({}),
  talkingPointsProps: PropTypes.shape({}),
  brandTermsProps: PropTypes.shape({}),
  channelPolicyProps: PropTypes.shape({}),
  blockedProps: PropTypes.shape({}),
  readabilityScoreProps: PropTypes.shape({}),
  seoTrackerProps: PropTypes.shape({}),
  plagiarismCheckerProps: PropTypes.shape({}),
  benchmarkProps: PropTypes.shape({}),
};

TextDetailsPanel.defaultProps = {
  className: null,
  classes: null,
  copyInputProps: null,
  scoreDetailsProps: null,
  scoreAdditionalFactorsProps: null,
  audienceChartProps: null,
  histogramAgeScoreProps: null,
  histogramGenderScoreProps: null,
  talkingPointsProps: null,
  brandTermsProps: null,
  channelPolicyProps: null,
  blockedProps: null,
  readabilityScoreProps: null,
  seoTrackerProps: null,
  plagiarismCheckerProps: null,
  benchmarkProps: null,
};
